import { Link } from "react-router-dom";
import { useState } from "react";
import BrandHeader from "../components/BrandHeader";
import Particles from "../components/Particles";
import BraceletAnatomyDiagram from "../components/BraceletAnatomyDiagram";
import { PRODUCT, BRAND } from "../lib/constants";
import { useSEO } from "../lib/seo";
import QuickCalculator from "../components/QuickCalculator";
import ExitIntentPopup from "../components/ExitIntentPopup";

const PRODUCT_IMG = "/product-bracelet.png";

// Zodiac quick reveal hints
const ZODIAC_TEASERS = [
  {
    sign: "Aries",
    symbol: "\u2648",
    hint: "Your dominant chakra may be the Root. Your stones could include Red Jasper and Carnelian.",
  },
  {
    sign: "Taurus",
    symbol: "\u2649",
    hint: "Your dominant chakra may be the Heart. Your stones could include Rose Quartz and Emerald.",
  },
  {
    sign: "Gemini",
    symbol: "\u264A",
    hint: "Your dominant chakra may be the Throat. Your stones could include Citrine and Blue Lace Agate.",
  },
  {
    sign: "Cancer",
    symbol: "\u264B",
    hint: "Your dominant chakra may be the Sacral. Your stones could include Moonstone and Pearl.",
  },
  {
    sign: "Leo",
    symbol: "\u264C",
    hint: "Your dominant chakra may be the Solar Plexus. Your stones could include Sunstone and Tiger Eye.",
  },
  {
    sign: "Virgo",
    symbol: "\u264D",
    hint: "Your dominant chakra may be the Throat. Your stones could include Peridot and Amazonite.",
  },
];

// Floating gold particles — see /app/frontend/src/components/Particles.jsx

export default function Landing() {
  useSEO({
    title: "Pancha Healing | India's First Crystal Prescription Brand",
    description:
      "Personalised Vedic crystal bracelets prescribed from your numerology, zodiac and five elements. Discover the exact stones your energy is asking for — with a printable Sacred Report.",
    canonical: "https://panchahealing.com/",
  });
  const [activeSign, setActiveSign] = useState(null);
  const [openWhyIdx, setOpenWhyIdx] = useState(null); // Why This Works accordion — all collapsed on load
  const teaser = ZODIAC_TEASERS.find((z) => z.sign === activeSign);

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <BrandHeader />
      <ExitIntentPopup enabled />

      {/* HERO - DARK MYSTICAL — split into two layers so the free calculator is above the fold on mobile */}
      <section className="relative overflow-hidden hero-mystical">
        <Particles variant="violet" />
        <div className="relative max-w-6xl mx-auto px-5 md:px-10 pt-8 md:pt-24 pb-8 md:pb-16">
          {/* LAYER 1 — compact lead: label + H1 + inline QuickCalculator (visible in the first mobile fold) */}
          <div className="max-w-2xl mx-auto text-center">
            {/* Gold label */}
            <div
              className="inline-flex items-center gap-3 text-[11px] md:text-xs tracking-[0.32em] uppercase fade-up"
              style={{ color: "#D4A855" }}
              data-testid="hero-label"
            >
              <span aria-hidden>&#10022;</span>
              India&apos;s First Crystal Prescription Brand
              <span aria-hidden>&#10022;</span>
            </div>

            {/* Headline — kept verbatim */}
            <h1
              className="font-display text-white mt-5 md:mt-7 fade-up delay-100 hero-headline-new"
              data-testid="hero-headline"
            >
              Something Has Been Blocking You Since The Day You Were Born.
            </h1>
          </div>

          {/* Inline free calculator — the primary above-the-fold CTA for ad traffic */}
          <div className="fade-up delay-200">
            <QuickCalculator compact />
          </div>

          {/* LAYER 2 — the rest of the original hero (image, sublines, secondary CTA, zodiac teaser, scroll hint) */}
          <div className="mt-5 md:mt-8 grid md:grid-cols-[55fr_45fr] gap-8 items-center">
            {/* RIGHT on desktop, FIRST on mobile — wrist photo */}
            <div className="order-1 md:order-2 md:pr-0">
              <img
                src="/images/hero-wrist.png"
                alt="Customer wearing her Pancha Healing prescribed bracelet — Ananya, Life Path 7"
                loading="eager"
                data-testid="hero-image"
                className="hero-photo"
              />
            </div>

            {/* LEFT on desktop, SECOND on mobile — sublines + secondary CTA */}
            <div className="order-2 md:order-1 text-center">
              {/* Subline 1 */}
              <p
                className="hero-subline-1 fade-up delay-200"
                data-testid="hero-subline-1"
              >
                Your birth date holds a secret about your energy, luck, and blocks most people never discover. We read it.
              </p>

              {/* Subline 2 */}
              <p
                className="hero-subline-2 fade-up delay-200"
                data-testid="hero-subline-2"
              >
                We read your numerology, map your chakras, and prescribe the exact stones your energy is asking for. No guessing. No trends. Just truth.
              </p>

              {/* Secondary CTA — for readers who scrolled past the calculator */}
              <div className="mt-8 fade-up delay-400 hero-cta-wrapper">
                <Link to="/prescription" className="hero-cta-pill" data-testid="hero-cta-reveal">
                  <span className="hero-cta-text">
                    Reveal My Crystal Prescription
                    <span className="arrow" aria-hidden>&nbsp;&rarr;</span>
                  </span>
                </Link>
                <p className="hero-trust-line" data-testid="hero-trust-line">
                  <span aria-hidden>&#10022;</span> Trusted by our first customers across India
                </p>
              </div>
            </div>
          </div>

          {/* Zodiac quick reveal teaser — tap-based (works on mobile touchscreens) */}
          <div className="mt-16 fade-up delay-300 text-center" data-testid="zodiac-teaser">
            <div className="text-xs tracking-[0.3em] uppercase" style={{ color: "#D4A855" }}>A glimpse &mdash; tap your sign</div>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-5 md:gap-7">
              {ZODIAC_TEASERS.slice(0, 3).map((z) => (
                <button
                  key={z.sign}
                  type="button"
                  onClick={() => setActiveSign((prev) => (prev === z.sign ? null : z.sign))}
                  className={`group relative w-16 h-16 md:w-[72px] md:h-[72px] rounded-full border flex items-center justify-center transition duration-300 ${activeSign === z.sign ? "scale-110" : "hover:scale-110"}`}
                  style={{
                    borderColor: activeSign === z.sign ? "#D4A855" : "rgba(212,168,85,0.45)",
                    background: activeSign === z.sign ? "rgba(212,168,85,0.18)" : "rgba(255,255,255,0.04)",
                    color: "#F6EFE0",
                    boxShadow: activeSign === z.sign ? "0 0 20px rgba(212,168,85,0.5)" : "none",
                  }}
                  data-testid={`zodiac-${z.sign.toLowerCase()}`}
                  aria-label={z.sign}
                  aria-pressed={activeSign === z.sign}
                >
                  <span className="text-2xl md:text-3xl leading-none" aria-hidden>{z.symbol}</span>
                  <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.22em] uppercase" style={{ color: "#D4A855" }}>
                    {z.sign}
                  </span>
                </button>
              ))}
              <Link
                to="/prescription"
                className="w-16 h-16 md:w-[72px] md:h-[72px] rounded-full border border-dashed flex items-center justify-center text-xs tracking-widest transition hover:scale-110"
                style={{ borderColor: "rgba(212,168,85,0.45)", color: "#D4A855" }}
                data-testid="zodiac-more"
              >
                MORE
              </Link>
            </div>

            {/* Teaser popup */}
            <div className="min-h-[92px] md:min-h-[72px] mt-10 flex items-center justify-center">
              {teaser ? (
                <div
                  className="max-w-md mx-auto px-5 py-3 rounded-xl border fade-up"
                  style={{
                    backgroundColor: "rgba(250, 248, 244, 0.08)",
                    borderColor: "rgba(212, 168, 85, 0.4)",
                    backdropFilter: "blur(8px)",
                    color: "#F6EFE0",
                  }}
                  data-testid="zodiac-teaser-card"
                >
                  <div className="text-[11px] tracking-[0.3em] uppercase" style={{ color: "#D4A855" }}>
                    {teaser.sign} &middot; a quick hint
                  </div>
                  <p className="mt-1.5 text-sm md:text-[15px] font-serif italic">{teaser.hint}</p>
                </div>
              ) : (
                <span className="text-xs italic" style={{ color: "rgba(246, 239, 224, 0.45)" }}>Tap a sign to peek at your reading</span>
              )}
            </div>
          </div>

          {/* Scroll hint */}
          <div className="mt-8 flex flex-col items-center gap-3" data-testid="scroll-hint">
            <span className="text-[10px] tracking-[0.4em] uppercase" style={{ color: "rgba(246, 239, 224, 0.6)" }}>Scroll to meet your stones</span>
            <span className="scroll-bob text-2xl" style={{ color: "#D4A855" }} aria-hidden>&darr;</span>
          </div>
        </div>
      </section>

      {/* CREDIBILITY BANNER */}
      <section className="bg-[#FAF7F2] border-y border-[rgba(212,168,85,0.3)]" data-testid="credibility-banner">
        <div className="max-w-6xl mx-auto px-5 md:px-10 py-5 md:h-20 md:py-0 flex items-center justify-center">
          <div className="flex flex-wrap items-center justify-center gap-x-6 md:gap-x-10 gap-y-2 text-center">
            {[
              "Rooted In Vedic Numerology",
              "Aligned To Your Chakra Profile",
              "Prescribed From Your Birth Details",
            ].map((label, i, arr) => (
              <div key={label} className="flex items-center gap-6">
                <span className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase font-medium" style={{ color: "#D4A855" }}>
                  {label}
                </span>
                {i < arr.length - 1 && <span className="hidden md:inline text-[#D4A855]/60">|</span>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE MIRROR MOMENT */}
      <section className="relative overflow-hidden py-20 md:py-24" data-testid="mirror-moment">
        <Particles variant="cream" />
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <div className="ph-overline">The Mirror Moment</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3" style={{ color: "#6A0DAD" }}>
              Does Any Of This Sound Like You?
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 md:gap-6">
            {[
              {
                svg: (
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#6A0DAD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M3 12c4-6 10 6 18 0" />
                    <path d="M3 17c4-4 10 4 18 0" />
                    <path d="M3 7c4-4 10 4 18 0" />
                  </svg>
                ),
                feeling: "I feel scattered and unable to focus",
                stones: "Your Crown and Third Eye chakras may need Clear Quartz and Amethyst",
              },
              {
                svg: (
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#6A0DAD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M12 21s-7-4.5-9-9a4.5 4.5 0 0 1 7-5l2 2 2-2a4.5 4.5 0 0 1 7 5c-2 4.5-9 9-9 9z" />
                    <path d="M12 7v14" strokeDasharray="2 2" />
                  </svg>
                ),
                feeling: "My heart feels closed or heavy",
                stones: "Rose Quartz and Green Aventurine may be calling you",
              },
              {
                svg: (
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#6A0DAD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M5 20 19 4" />
                    <path d="M5 20l1.5-4.5L11 14" />
                    <path d="M19 4l-1.5 4.5L13 10" />
                    <circle cx="7" cy="18" r="1.2" />
                    <circle cx="17" cy="6" r="1.2" />
                  </svg>
                ),
                feeling: "I'm stuck and need momentum",
                stones: "Citrine and Carnelian could reignite your Solar Plexus",
              },
              {
                svg: (
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#6A0DAD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M12 3l2.4 5.4L20 9l-4 4 1 6-5-3-5 3 1-6-4-4 5.6-.6L12 3z" opacity="0.55" />
                    <path d="M5 21h14" strokeDasharray="3 3" />
                  </svg>
                ),
                feeling: "Anxiety and overthinking won't stop",
                stones: "Aquamarine and Amethyst may bring the calm you seek",
              },
            ].map((card, i) => (
              <div key={card.feeling} className="ph-card p-7 flex flex-col md:flex-row gap-4 items-start" data-testid={`mirror-card-${i}`}>
                <div
                  className="shrink-0 mx-auto md:mx-0 flex items-center justify-center"
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "12px",
                    background: "#EEEDFE",
                    flexShrink: 0,
                  }}
                  aria-hidden
                >
                  {card.svg}
                </div>
                <div className="flex-1 text-center md:text-left">
                  <p className="font-serif italic text-xl text-[#2C0A5E] leading-snug">&ldquo;{card.feeling}&rdquo;</p>
                  <div className="ph-divider-cream my-4 w-10 mx-auto md:mx-0" />
                  <p className="text-sm text-[#6B5E7E] leading-relaxed">{card.stones}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="font-serif italic text-lg md:text-xl" style={{ color: "#D4A855" }}>
              If you nodded to even one &mdash; your prescription is waiting.
            </p>
            <Link to="/prescription" className="ph-cta mt-7" data-testid="mirror-cta">
              Find My Stones
              <span className="arrow" aria-hidden>&rarr;</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Section divider — violet line with centred gold ornament (cream boundary) */}
      <div aria-hidden className="ph-section-divider" data-testid="section-divider-after-mirror" />

      {/* PRESCRIPTION NOT SELECTION */}
      <section
        className="relative overflow-hidden py-20"
        style={{ backgroundColor: "#FAF7F2" }}
        data-testid="prescription-not-selection"
      >
        <Particles variant="cream" />
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-5xl" style={{ color: "#6A0DAD" }}>
              Prescription. Not Selection.
            </h2>
            <p
              className="font-serif italic mt-4"
              style={{ color: "#D4A855", fontSize: "15px" }}
            >
              This is the difference between a bracelet that looks good and one that actually works.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: "\u{1F3EA}",
                heading: "Any Crystal Shop",
                headingColor: "#6A0DAD",
                borderWidth: "1px",
                bgColor: "#ffffff",
                bodyColor: "#444444",
                text: "Picks crystals based on what looks beautiful, what is trending, or what is in stock. Your energy blueprint is never read. Your chakras are never mapped.",
                resultText: "Result: A beautiful bracelet that does nothing.",
                resultColor: "#6A0DAD",
              },
              {
                icon: "\u{1F52E}",
                heading: "Pancha Healing",
                headingColor: "#D4A855",
                borderWidth: "2px",
                bgColor: "#2C0A5E",
                bodyColor: "#FAF7F2",
                text: "Reads your Life Path number, maps your dominant and blocked chakras, and selects only the crystals whose frequencies directly address your specific energetic pattern.",
                resultText: "Result: A prescription that was written for you alone.",
                resultColor: "#D4A855",
              },
              {
                icon: "\u{1F4FF}",
                heading: "The Vedic Way",
                headingColor: "#6A0DAD",
                borderWidth: "1px",
                bgColor: "#ffffff",
                bodyColor: "#444444",
                text: "Ancient Vedic healers never prescribed the same stone twice without reading the person first. Every crystal has a frequency. Every person has a pattern. Matching them is the science.",
                resultText: "Result: Healing that feels personal.",
                resultColor: "#D4A855",
              },
            ].map((card, i) => {
              const orderClass = i === 1 ? "order-1 md:order-2" : i === 0 ? "order-2 md:order-1" : "order-3";
              return (
              <div
                key={card.heading}
                style={{
                  backgroundColor: card.bgColor,
                  border: `${card.borderWidth} solid #D4A855`,
                  borderRadius: "12px",
                  padding: "24px",
                  boxShadow:
                    card.borderWidth === "2px"
                      ? "0 18px 40px rgba(44,10,94,0.35), 0 0 0 1px rgba(212,168,85,0.25)"
                      : "0 6px 20px rgba(44,10,94,0.06)",
                  transform: card.borderWidth === "2px" ? "scale(1.02)" : "none",
                }}
                className={`text-center ${orderClass}`}
                data-testid={`pns-card-${i}`}
              >
                <div className="text-[32px] leading-none" aria-hidden>{card.icon}</div>
                <h3
                  className="font-display mt-4"
                  style={{ color: card.headingColor, fontSize: "20px", letterSpacing: "0.02em" }}
                >
                  {card.heading}
                </h3>
                <p className="text-sm leading-relaxed mt-4" style={{ color: card.bodyColor }}>
                  {card.text}
                </p>
                <p
                  className="font-serif italic mt-5"
                  style={{ color: card.resultColor, fontSize: "13px" }}
                >
                  {card.resultText}
                </p>
              </div>
            );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        className="relative overflow-hidden py-16 md:py-20"
        style={{ backgroundColor: "#6A0DAD" }}
        data-testid="how-it-works"
      >
        <Particles variant="violet" />
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <div className="ph-overline" style={{ color: "#D4A855" }}>The Journey</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3 text-white">How It Works</h2>
          </div>

          {/* Horizontal step flow with dashed gold connectors */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-center gap-6 md:gap-3">
            {[
              {
                svg: (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M4 20h4l10-10-4-4L4 16v4z" />
                    <path d="M14 6l4 4" />
                  </svg>
                ),
                title: "Share your details",
                desc: "Name, date of birth and healing intentions",
              },
              {
                svg: (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <circle cx="12" cy="13" r="7" />
                    <path d="M9 11l1.5 1.5L15 8" />
                    <path d="M12 3v2 M5 5l1.5 1.5 M19 5l-1.5 1.5" />
                  </svg>
                ),
                title: "We prescribe your crystals",
                desc: "Chaldean numerology and Vedic Jyotish analysis",
              },
              {
                svg: (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M4 9l8-5 8 5-8 5-8-5z" />
                    <path d="M4 9v6l8 5 8-5V9" />
                  </svg>
                ),
                title: "Handcrafted and delivered",
                desc: "Your sacred bracelet arrives in 5 to 7 days",
              },
            ].map((step, i, arr) => (
              <div key={step.title} className="contents">
                <div
                  className="flex flex-col items-center md:flex-1 hiw-step px-3 md:px-2 text-center w-full"
                  style={{ animationDelay: `${i * 200}ms` }}
                  data-testid={`how-step-${i + 1}`}
                >
                  <div
                    className="flex items-center justify-center relative"
                    style={{
                      width: "56px",
                      height: "56px",
                      borderRadius: "50%",
                      background: "#6A0DAD",
                      border: "2px solid #D4A855",
                      boxShadow: "0 0 0 4px rgba(212,168,85,0.18)",
                    }}
                  >
                    {step.svg}
                    <span
                      className="absolute -bottom-2 -right-2 text-[10px] font-medium"
                      style={{
                        background: "#D4A855",
                        color: "#2C0A5E",
                        borderRadius: "999px",
                        padding: "2px 6px",
                        letterSpacing: "0.08em",
                      }}
                    >
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="font-display mt-5" style={{ color: "#D4A855", fontSize: "14px", letterSpacing: "0.06em" }}>
                    {step.title}
                  </h3>
                  <p className="font-serif italic mt-2" style={{ color: "#FAF7F2", fontSize: "13px", maxWidth: "220px" }}>
                    {step.desc}
                  </p>
                </div>

                {/* Connector between steps: horizontal on desktop, vertical on mobile */}
                {i < arr.length - 1 && (
                  <>
                    <div className="hidden md:block self-center hiw-connector-h" aria-hidden />
                    <div className="block md:hidden mx-auto hiw-connector-v" aria-hidden />
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/prescription"
              className="how-it-works-cta"
              data-testid="how-cta"
            >
              <span style={{ whiteSpace: "nowrap" }}>
                Begin My Journey
                <span className="arrow" aria-hidden>&nbsp;&rarr;</span>
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Soft cream-boundary divider between two adjacent cream sections */}
      <div aria-hidden className="ph-section-divider" data-testid="section-divider-before-elements" />

      {/* THE FIVE ELEMENTS OF YOUR PRESCRIPTION */}
      <section
        className="relative overflow-hidden py-16 md:py-20"
        style={{ backgroundColor: "#F5F0E8" }}
        data-testid="five-elements"
      >
        <Particles variant="cream" />
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <h2 className="font-display" style={{ color: "#6A0DAD", fontFamily: "'Cormorant Garamond', serif", fontSize: "26px", letterSpacing: "0.12em", fontWeight: 700 }}>
              THE FIVE ELEMENTS OF YOUR PRESCRIPTION
            </h2>
            <p
              className="mt-3 italic"
              style={{ color: "#888888", fontFamily: "'Cormorant Garamond', serif", fontSize: "16px" }}
            >
              Every bracelet is rooted in the Pancha Mahabhutas &mdash; the five sacred forces that govern your energy
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            {/* Pentagon star connector behind circles (desktop only) — each circle linked to two non-adjacent across */}
            <svg
              className="absolute left-0 hidden md:block pointer-events-none"
              viewBox="0 0 100 80"
              preserveAspectRatio="none"
              aria-hidden
              style={{ top: 0, width: "100%", height: "100px", opacity: 0.35, zIndex: 0 }}
            >
              {/* Circle centres in viewBox: (10,40), (30,40), (50,40), (70,40), (90,40) */}
              {/* Non-adjacent pairs: 0-2, 0-3, 1-3, 1-4, 2-4 — drawn as quadratic curves arcing above/below */}
              <path d="M 10 40 Q 30 6 50 40" fill="none" stroke="#D4A855" strokeWidth="1" />
              <path d="M 50 40 Q 70 6 90 40" fill="none" stroke="#D4A855" strokeWidth="1" />
              <path d="M 30 40 Q 50 76 70 40" fill="none" stroke="#D4A855" strokeWidth="1" />
              <path d="M 10 40 Q 40 78 70 40" fill="none" stroke="#D4A855" strokeWidth="1" />
              <path d="M 30 40 Q 60 2 90 40" fill="none" stroke="#D4A855" strokeWidth="1" />
            </svg>

            <div className="relative grid grid-cols-6 md:grid-cols-5 gap-y-6 md:gap-4 justify-items-center" style={{ zIndex: 1 }}>
              {[
                {
                  name: "Prithvi",
                  english: "Earth",
                  color: "#6B4D2E",
                  crystal: "Red Jasper",
                  shape: <polygon points="36,15 21,40 51,40" fill="white" />,
                },
                {
                  name: "Jala",
                  english: "Water",
                  color: "#1D5FA5",
                  crystal: "Aquamarine",
                  shape: <path d="M22 18 a14 14 0 1 0 14 22 a10 10 0 1 1 -14 -22z" fill="white" />,
                },
                {
                  name: "Agni",
                  english: "Fire",
                  color: "#C13B1B",
                  crystal: "Carnelian",
                  shape: <polygon points="36,17 21,42 51,42" fill="white" />,
                },
                {
                  name: "Vayu",
                  english: "Air",
                  color: "#4A9E6B",
                  crystal: "Green Aventurine",
                  shape: (
                    <>
                      <polygon points="36,15 21,40 51,40" fill="none" stroke="white" strokeWidth="2.2" />
                      <polygon points="36,45 21,20 51,20" fill="none" stroke="white" strokeWidth="2.2" />
                    </>
                  ),
                },
                {
                  name: "Akasha",
                  english: "Space",
                  color: "#534AB7",
                  crystal: "Amethyst",
                  shape: <circle cx="36" cy="30" r="13" fill="none" stroke="white" strokeWidth="2.4" />,
                },
              ].map((el, idx) => {
                // 3+2 row on mobile (grid-cols-6): items 0,1,2 fill row 1 (cols 1-2, 3-4, 5-6).
                // Items 3,4 centered in row 2 at col-start 2 and 4.
                const mobileLayout =
                  idx === 3 ? "col-span-2 col-start-2" :
                  idx === 4 ? "col-span-2 col-start-4" :
                  "col-span-2";
                return (
                <div
                  key={el.name}
                  className={`text-center five-elem ${mobileLayout} md:col-span-1 md:col-start-auto`}
                  data-testid={`element-${el.english.toLowerCase()}`}
                >
                  <div
                    className="mx-auto flex items-center justify-center transition duration-200"
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "50%",
                      background: el.color,
                    }}
                  >
                    <svg viewBox="0 0 72 60" width="36" height="30" aria-hidden>
                      {el.shape}
                    </svg>
                  </div>
                  <div className="mt-3 font-display" style={{ color: "#2C0A5E", fontSize: "13px", letterSpacing: "0.04em" }}>
                    {el.english}
                  </div>
                  <div className="italic" style={{ color: "#888888", fontFamily: "'Cormorant Garamond', serif", fontSize: "11px" }}>
                    {el.name}
                  </div>
                  <div className="mt-1" style={{ color: "#6A0DAD", fontFamily: "'Cormorant Garamond', serif", fontSize: "12px" }}>
                    {el.crystal}
                  </div>
                </div>
              );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS - WHAT YOUR PRESCRIPTION ACTUALLY DOES */}
      <section
        className="relative py-20 overflow-hidden"
        style={{ backgroundColor: "#6A0DAD" }}
        data-testid="benefits"
      >
        <Particles variant="violet" />
        {/* Decorative top + bottom gold dividers */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-px w-40 md:w-64" style={{ background: "linear-gradient(90deg, transparent, #D4A855, transparent)" }} />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-px w-40 md:w-64" style={{ background: "linear-gradient(90deg, transparent, #D4A855, transparent)" }} />

        <div className="relative max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <div className="ph-overline" style={{ color: "#D4A855" }}>The Inner Shift</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3" style={{ color: "#D4A855" }}>
              What Your Prescription Actually Does
            </h2>
            <p className="font-serif italic text-base md:text-lg mt-4" style={{ color: "#D4A855" }}>
              Rooted in Vedic science. Felt in real life.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: "\u{1F9E0}",
                title: "Mental Clarity",
                text: "Your prescribed crystals are chosen to support your Crown and Third Eye chakras, traditionally linked with focus, clarity and calm.",
              },
              {
                icon: "\u{1F4B0}",
                title: "Abundance Flow",
                text: "Citrine, Pyrite and Green Aventurine matched to your Life Path support your Solar Plexus, traditionally linked with confidence, drive and abundance.",
              },
              {
                icon: "\u2764\uFE0F",
                title: "Emotional Release",
                text: "Rose Quartz and Amethyst matched to your chakras, traditionally linked with calm, openness and self-trust.",
              },
            ].map((b, i) => (
              <div
                key={b.title}
                className="relative rounded-2xl p-8 transition duration-300 hover:-translate-y-1"
                style={{
                  backgroundColor: "#2C0A5E",
                  border: "1px solid #D4A855",
                  boxShadow: "0 14px 34px rgba(0,0,0,0.25), 0 0 0 1px rgba(212,168,85,0.18)",
                }}
                data-testid={`benefit-${i}`}
              >
                {/* Corner gold accent */}
                <div className="absolute top-0 left-0 w-10 h-10 rounded-tl-2xl border-t border-l" style={{ borderColor: "#D4A855" }} />
                <div className="absolute bottom-0 right-0 w-10 h-10 rounded-br-2xl border-b border-r" style={{ borderColor: "#D4A855" }} />
                <div className="text-4xl" aria-hidden>{b.icon}</div>
                <h3 className="font-serif text-2xl mt-5" style={{ color: "#D4A855" }}>{b.title}</h3>
                <div className="ph-divider my-4 w-10" />
                <p className="text-sm leading-relaxed" style={{ color: "#FAF7F2" }}>{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 90-DAY FOMO BANNER */}
      <section
        className="relative overflow-hidden py-16 md:py-20 text-center"
        style={{ backgroundColor: "#2C0A5E" }}
        data-testid="fomo-90days"
      >
        <Particles variant="violet" />
        <div className="relative max-w-3xl mx-auto px-5 md:px-10">
          <p
            className="font-script leading-tight"
            style={{ color: "#D4A855", fontSize: "clamp(2rem, 5vw, 3.25rem)" }}
          >
            Give it 90 days. Notice what shifts.
          </p>
          <p
            className="font-serif italic mt-5"
            style={{ color: "#FAF7F2", fontSize: "16px" }}
          >
            Your numbers were written the day you were born. Reading them takes 2 minutes.
          </p>
        </div>
      </section>

      {/* ANCIENT LOGIC BANNER */}
      <div
        style={{
          backgroundColor: "#6A0DAD",
          height: "80px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
        data-testid="ancient-logic-banner"
      >
        <p
          className="font-serif italic text-center px-5"
          style={{ color: "#D4A855", fontSize: "18px" }}
        >
          The ancient logic behind your prescription &#10026;
        </p>
      </div>

      {/* WHY THIS WORKS - THE ANCIENT LOGIC */}
      <section className="relative overflow-hidden py-20" style={{ backgroundColor: "#FAF7F2" }} data-testid="why-this-works">
        <Particles variant="cream" />
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <div className="text-center mb-14">
            <h2 className="font-display text-3xl md:text-5xl leading-tight" style={{ color: "#6A0DAD" }}>
              Why This Works
              <span className="block text-xl md:text-3xl mt-3 font-normal">The Ancient Logic Behind Your Prescription</span>
            </h2>
            <p className="font-serif italic text-base md:text-lg mt-6 max-w-2xl mx-auto" style={{ color: "#D4A855" }}>
              This is not mysticism. This is a system &mdash; thousands of years old, mathematically precise, and deeply personal.
            </p>
          </div>

          <div>
            {[
              {
                icon: "\u{1F522}",
                title: "Numerology Is Not Astrology",
                paragraphs: [
                  "Numerology is not fortune telling. It is a number system.",
                  "Your birth date is not random. It carries a number \u2014 your Life Path \u2014 that shows what energy you naturally have, and what you\u2019re missing.",
                  "This number does not change. It doesn\u2019t depend on planets. It is math \u2014 and it tells us exactly which crystals your body has been asking for.",
                ],
              },
              {
                icon: "\u{1F48E}",
                title: "Crystals Carry Frequency",
                paragraphs: [
                  "Every crystal on Earth vibrates at its own frequency. That\u2019s not spiritual talk \u2014 that\u2019s why quartz is used in watches, phones and hospital machines.",
                  "Your body has a frequency too \u2014 your chakras. When a crystal touches you, the two frequencies talk to each other. That\u2019s the whole idea behind crystal healing.",
                  "Clear Quartz amplifies. Amethyst calms. Citrine activates. Black Tourmaline protects. These are not made-up properties \u2014 they\u2019ve been written in Vedic texts for thousands of years.",
                ],
              },
              {
                icon: "\u{1F300}",
                title: "The Chakra-Numerology Bridge",
                paragraphs: [
                  "Here is where our system is unique.",
                  "Your Life Path number points straight to one dominant chakra. A Life Path 1 is Solar Plexus. A Life Path 2 is Heart. A Life Path 7 is Third Eye.",
                  "When we know your dominant chakra, we also know which chakras are tired or blocked \u2014 and the exact crystals that bring you back into balance.",
                  "Numerology gives us the map. Chakras show us where the block is. Crystals do the work.",
                ],
              },
              {
                icon: "\u2753",
                title: "Why Most Crystal Bracelets Don\u2019t Work",
                paragraphs: [
                  "A Rose Quartz bracelet from a store looks beautiful. But if your Heart chakra isn\u2019t the one that needs help \u2014 it won\u2019t do much.",
                  "That\u2019s why people buy crystal after crystal and feel nothing.",
                  "The crystal was fine. The prescription was missing. At Pancha Healing, we read your numbers first \u2014 then we pick the stones.",
                ],
              },
              {
                icon: "\u{1F33F}",
                title: "The Five Elements - What Pancha Means",
                paragraphs: [
                  "Pancha means five in Sanskrit \u2014 the five elements of Vedic tradition: Prithvi (Earth), Jal (Water), Agni (Fire), Vayu (Air), Akasha (Space).",
                  "Every person is a unique mix of these five. Every chakra is ruled by one. Every crystal belongs to one or more.",
                  "When your five elements go out of balance, you feel it \u2014 as tiredness, worry, money blocks, or things that just won\u2019t move. Bringing them back into balance is what your bracelet does.",
                ],
              },
            ].map((block, idx, arr) => {
              const isOpen = openWhyIdx === idx;
              return (
              <div key={block.title}>
                <div
                  className="px-2 md:px-4"
                  style={{ paddingTop: idx === 0 ? "0" : "24px", paddingBottom: "24px" }}
                  data-testid={`why-works-${idx}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenWhyIdx(isOpen ? null : idx)}
                    aria-expanded={isOpen}
                    aria-controls={`why-works-body-${idx}`}
                    className="w-full text-left flex items-start gap-5 flex-col md:flex-row"
                    data-testid={`why-works-toggle-${idx}`}
                  >
                    <div className="text-4xl md:text-5xl shrink-0" style={{ color: "#D4A855" }} aria-hidden>{block.icon}</div>
                    <div className="flex-1 w-full">
                      <div className="flex items-center gap-3 justify-between">
                        <h3
                          className="font-serif font-semibold text-2xl md:text-3xl leading-tight text-center md:text-left flex-1"
                          style={{ color: "#2C0A5E" }}
                        >
                          {block.title}
                        </h3>
                        <span
                          className="shrink-0 inline-flex items-center justify-center"
                          aria-hidden
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            border: "1.5px solid #D4A855",
                            color: "#6A0DAD",
                            fontSize: "22px",
                            lineHeight: 1,
                            fontWeight: 300,
                            transition: "transform 200ms ease",
                            transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                          }}
                        >
                          +
                        </span>
                      </div>
                    </div>
                  </button>
                  <div
                    id={`why-works-body-${idx}`}
                    role="region"
                    className="why-acc-body"
                    style={{
                      maxHeight: isOpen ? "2000px" : "0px",
                      overflow: "hidden",
                      transition: "max-height 300ms ease-in-out",
                    }}
                  >
                    <div className="space-y-4 mt-5 text-center md:text-left md:pl-[68px]">
                      {block.paragraphs.map((p, pi) => (
                        <p
                          key={`${block.title}-p${pi}`}
                          className="text-[15px] md:text-base leading-relaxed"
                          style={
                            pi === 0
                              ? { color: "#D4A855", fontWeight: 700 }
                              : { color: "#333333" }
                          }
                        >
                          {p}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
                {idx < arr.length - 1 && (
                  <div
                    className="h-px w-full"
                    style={{ background: "linear-gradient(90deg, transparent, #D4A855, transparent)" }}
                  />
                )}
              </div>
            );
            })}
          </div>

          <div className="text-center mt-14">
            <p
              style={{
                fontFamily: "Georgia, serif",
                fontStyle: "italic",
                color: "#D4A855",
                fontSize: "20px",
                textAlign: "center",
                lineHeight: "1.5",
              }}
            >
              The prescription was always there. We simply learned to read it.
            </p>
            <div className="mt-10 mx-auto h-px w-24 ph-divider-cream" />
          </div>
        </div>
      </section>

      {/* QuickCalculator now lives inside the hero (above the fold). */}

      {/* HOW WE PRESCRIBE */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundColor: "#6A0DAD", paddingTop: "80px", paddingBottom: "80px" }}
        data-testid="how-we-prescribe"
      >
        <Particles variant="violet" />
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-5xl text-white">
              How We Prescribe Your Crystals
            </h2>
            <p
              className="font-serif italic mt-4"
              style={{ color: "#D4A855", fontSize: "16px" }}
            >
              Five sacred parameters. One precise prescription. Unique to you alone.
            </p>
          </div>

          <div
            className="grid md:grid-cols-2"
            style={{ gap: "24px" }}
          >
            {[
              {
                icon: "\u{1F522}",
                title: "Your Life Path Number",
                body: "One number, calculated from your birth date, that reveals your dominant chakra and the exact crystals your body has been asking for \u2014 often for years.",
                italicNote: "Your prescription cannot begin without this number.",
              },
              {
                icon: "\u2648",
                title: "Your Zodiac Sign",
                body: "Your rashi shows your natural element and how you feel things. Paired with your Life Path, we get a much sharper picture of you.",
                italicNote: "Same zodiac sign. Different Life Path. Completely different prescription.",
              },
              {
                icon: "\u{1F300}",
                title: "Your Dominant Chakra",
                body: "Every person has one chakra that\u2019s always on \u2014 and one that\u2019s always blocked. We find both from your numbers and pick crystals that bring you back into balance.",
                italicNote: "This is where your prescription becomes truly personal.",
              },
              {
                icon: "\u{1F524}",
                title: "Your Name Number",
                body: "The number of your birth name tells us if your inside energy and outside personality are in sync \u2014 or fighting each other. This mismatch is often the real reason things stay stuck.",
                italicNote: "This is the layer most crystal brands never read.",
              },
              {
                icon: "\u{1F319}",
                title: "Your Birth Month Crystal",
                body: "Every birth month has its own stone in Vedic tradition. Yours is placed in your bracelet first \u2014 as the anchor \u2014 before any other reading is done.",
                isLast: true,
                subListTwoCol: [
                  "January \u2192 Garnet",
                  "February \u2192 Amethyst",
                  "March \u2192 Aquamarine",
                  "April \u2192 Clear Quartz",
                  "May \u2192 Green Aventurine",
                  "June \u2192 Moonstone",
                  "July \u2192 Ruby",
                  "August \u2192 Peridot",
                  "September \u2192 Lapis Lazuli",
                  "October \u2192 Rose Quartz",
                  "November \u2192 Citrine",
                  "December \u2192 Turquoise",
                ],
              },
            ].map((card, i) => (
              <div
                key={card.title}
                className={`relative overflow-hidden ${card.isLast ? "md:col-span-2 md:max-w-[calc(50%-12px)] md:mx-auto md:w-full" : ""}`}
                style={{
                  backgroundColor: "#2C0A5E",
                  border: "1px solid #D4A855",
                  borderRadius: "12px",
                  padding: "32px",
                }}
                data-testid={`prescribe-card-${i}`}
              >
                <div className="relative z-10">
                  <div className="text-4xl" aria-hidden>{card.icon}</div>
                  <h3
                    className="font-display mt-4"
                    style={{ color: "#D4A855", fontSize: "18px", letterSpacing: "0.02em" }}
                  >
                    {card.title}
                  </h3>
                  <p
                    className="font-serif italic mt-4 leading-relaxed"
                    style={{ color: "#FAF7F2", fontSize: "15px" }}
                  >
                    {card.body}
                  </p>
                {card.italicNote && (
                  <p
                    className="font-serif italic mt-4"
                    style={{ color: "#D4A855", fontSize: "13px" }}
                  >
                    {card.italicNote}
                  </p>
                )}
                {card.subListTwoCol && (
                  <ul
                    className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5"
                    style={{ color: "#D4A855", fontSize: "13px" }}
                  >
                    {card.subListTwoCol.map((li) => (
                      <li key={li}>{li}</li>
                    ))}
                  </ul>
                )}
                </div>
              </div>
            ))}
          </div>

          {/* Closing banner */}
          <div
            className="mt-10 text-center"
            style={{
              backgroundColor: "#2C0A5E",
              border: "1px solid #D4A855",
              borderRadius: "12px",
              padding: "40px",
            }}
            data-testid="prescribe-banner"
          >
            <p
              className="font-script leading-tight"
              style={{ color: "#D4A855", fontSize: "clamp(2rem, 5vw, 3.25rem)" }}
            >
              All five parameters are read before a single crystal is chosen.
            </p>
            <p
              className="mt-4 max-w-xl mx-auto"
              style={{ color: "#FAF7F2", fontSize: "14px" }}
            >
              This is why no two Pancha Healing bracelets are ever the same.
            </p>
            <Link
              to="/prescription"
              className="ph-cta mt-8"
              style={{ backgroundColor: "#6A0DAD" }}
              data-testid="prescribe-cta"
            >
              Begin My Prescription
              <span className="arrow" aria-hidden>&rarr;</span>
            </Link>
          </div>
        </div>
      </section>

      {/* CRYSTAL MEANINGS — Bracelet Anatomy Diagram */}
      <section className="relative overflow-hidden py-16 md:py-20" data-testid="crystal-meanings">
        <Particles variant="cream" />
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <div className="ph-overline">Your Crystals</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3" style={{ color: "#6A0DAD" }}>What&apos;s In Your Bracelet</h2>
          </div>

          <BraceletAnatomyDiagram />

          <div className="text-center mt-10">
            <Link to="/prescription" className="ph-cta" data-testid="crystals-cta">
              Discover Which Are Yours
              <span className="arrow" aria-hidden>&rarr;</span>
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative overflow-hidden py-16 md:py-20 bg-white" data-testid="testimonials">
        <Particles variant="cream" />
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          {/* Review summary bar */}
          <div className="text-center mb-10" data-testid="review-summary">
            <div className="inline-flex items-center gap-2 text-2xl md:text-3xl" style={{ color: "#D4A855" }}>
              {"\u2605\u2605\u2605\u2605\u2605"}
            </div>
            <div className="font-display text-xl md:text-2xl mt-3" style={{ color: "#2C0A5E" }}>
              &#10022; Every Prescription Is Personal &middot; Crafted Only For You
            </div>
          </div>

          <div className="text-center mb-12">
            <div className="ph-overline">Voices</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3" style={{ color: "#6A0DAD" }}>What Our Customers Say</h2>
          </div>

          {/* Three testimonial cards — photo-background layout */}
          <div className="grid md:grid-cols-3 gap-5 md:gap-6" data-testid="testimonial-cards">
            {[
              {
                src: "/images/wrist-testimonial.png",
                alt: "Ananya wearing her Pancha Healing bracelet",
                quote: "I feel so calm and clear now. It's like this bracelet understands me.",
                name: "Ananya",
                meta: "Life Path 7",
                objectPosition: "center 20%",
              },
              {
                src: "/images/testimonial-priya.png",
                alt: "Priya wearing her Pancha Healing bracelet",
                quote: "I was skeptical at first, but my bracelet arrived with such care and the prescription card explained everything. I haven't taken it off.",
                name: "Priya S.",
                meta: "Mumbai",
                objectPosition: "center 25%",
              },
              {
                src: "/images/testimonial-meera.png",
                alt: "Meera outdoors wearing her Pancha Healing bracelet",
                quote: "Beautiful packaging, fast delivery, and the crystals are exactly what I needed. Worth every rupee.",
                name: "Meera T.",
                meta: "Delhi",
                objectPosition: "center 20%",
              },
            ].map((t) => (
              <article
                key={t.name}
                className="testimonial-photo-card"
                data-testid={`testimonial-card-${t.name.replace(/\W+/g, "-").toLowerCase()}`}
              >
                <img
                  src={t.src}
                  alt={t.alt}
                  loading="lazy"
                  className="testimonial-photo-card__img"
                  style={{ objectPosition: t.objectPosition }}
                />
                <div className="testimonial-photo-card__overlay" aria-hidden />
                <div className="testimonial-photo-card__content">
                  <div className="testimonial-photo-card__stars" aria-label="5 stars">
                    {"\u2605\u2605\u2605\u2605\u2605"}
                  </div>
                  <p className="testimonial-photo-card__quote">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div className="testimonial-photo-card__attribution">
                    <span className="testimonial-photo-card__name">{t.name}</span>
                    <span className="testimonial-photo-card__meta"> · {t.meta}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Founder guarantee block */}
          <div
            className="mt-12 rounded-2xl p-8 md:p-12 text-center"
            style={{
              background: "linear-gradient(135deg, #2C0A5E 0%, #6A0DAD 100%)",
              border: "1px solid rgba(212,168,85,0.4)",
            }}
            data-testid="founder-guarantee"
          >
            <p className="font-serif italic text-xl md:text-2xl leading-relaxed max-w-3xl mx-auto" style={{ color: "#FAF7F2" }}>
              &ldquo;Every bracelet I send carries my name and my reading. If it does not feel right &mdash; I will refund you fully. No forms. No questions. Just WhatsApp me.&rdquo;
            </p>
            <div className="mt-5 text-xs tracking-[0.3em] uppercase" style={{ color: "#D4A855" }}>
              &mdash; Madhuram, Founder
            </div>
          </div>

          {/* Trust icons row */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 md:gap-10" data-testid="trust-icons-row">
            {[
              { icon: "\u{1F512}", label: "Encrypted Payment" },
              { icon: "\u{1F4E6}", label: "Tracked Shipping" },
              { icon: "\u{1F49B}", label: "7-Day Full Refund" },
            ].map((t) => (
              <div key={t.label} className="inline-flex items-center gap-2 text-sm text-[#2C0A5E]">
                <span className="text-lg" aria-hidden>{t.icon}</span>
                <span>{t.label}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/prescription" className="ph-cta" data-testid="testimonial-cta">
              I&apos;m Ready For Mine
              <span className="arrow" aria-hidden>&rarr;</span>
            </Link>
          </div>
        </div>
      </section>

      {/* SINGLE PRODUCT */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="text-center mb-10">
            <div className="ph-overline">One bracelet. One price. Everything included.</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3" style={{ color: "#6A0DAD" }}>Your Sacred Crystal Bracelet.</h2>
          </div>

          <div className="ph-card overflow-hidden grid md:grid-cols-2" data-testid={`product-${PRODUCT.id}`}>
            <div className="relative min-h-[360px]">
              <img src={PRODUCT_IMG} alt={PRODUCT.name} className="w-full h-full object-cover absolute inset-0" />
              <div className="absolute top-5 left-5 bg-[#D4A855] text-[#2C0A5E] text-xs font-medium px-3 py-1 rounded-full tracking-wider uppercase">
                Complete Blueprint
              </div>
            </div>
            <div className="p-8 md:p-10 flex flex-col">
              <div className="ph-overline">Pancha Healing</div>
              <h3 className="font-serif text-3xl text-[#2C0A5E] mt-1">{PRODUCT.name}</h3>
              <div className="font-serif text-4xl md:text-5xl text-[#6A0DAD] mt-4" data-testid="product-price">Rs {PRODUCT.price}</div>
              <p
                className="font-serif italic mt-2"
                style={{ color: "#D4A855", fontSize: "13px" }}
                data-testid="product-handcraft-line"
              >
                Handcrafted one at a time. Your bracelet is made only after your reading.
              </p>
              <p className="mt-3 text-xs font-medium tracking-wider" style={{ color: "#D4A855" }}>
                &#10022; Trusted by our first customers across India
              </p>
              <p className="text-sm text-[#6B5E7E] mt-2">Everything you need to wear your prescription.</p>
              <ul className="mt-6 space-y-2.5">
                {PRODUCT.includes.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[#2C0A5E] text-sm">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 bg-[#D4A855]" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/prescription" className="ph-cta mt-8 self-start" data-testid="choose-product">
                Reveal My Crystal Prescription
                <span className="arrow" aria-hidden>&rarr;</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER STORY */}
      <section className="relative overflow-hidden py-20 bg-[#FAF7F2]" data-testid="founder-story">
        <Particles variant="cream" />
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-5xl" style={{ color: "#6A0DAD" }}>
              Why I Built Pancha Healing
            </h2>
          </div>

          <div className="grid md:grid-cols-[260px_1fr] gap-8 md:gap-12 items-start">
            {/* Founder photo placeholder */}
            <div className="flex justify-center md:justify-start">
              <div
                className="w-52 h-52 md:w-[260px] md:h-[260px] rounded-full overflow-hidden flex items-center justify-center"
                style={{
                  background: "radial-gradient(circle at 30% 20%, #6A0DAD, #2C0A5E)",
                  border: "2px solid rgba(212,168,85,0.55)",
                  boxShadow: "0 10px 32px rgba(44,10,94,0.25)",
                }}
                data-testid="founder-photo"
              >
                <img src="/pancha-logo.png" alt="Madhuram, Founder" className="w-full h-full object-cover opacity-95" />
              </div>
            </div>

            <div className="text-[#2C0A5E]">
              <div className="space-y-5 text-base md:text-[17px] leading-relaxed" style={{ color: "#333333" }}>
                <p className="font-serif italic text-xl md:text-2xl" style={{ color: "#2C0A5E" }}>
                  &ldquo;I didn&apos;t start this as a business. I started it as a search.&rdquo;
                </p>
                <p>
                  For years I felt stuck &mdash; in my energy, my decisions, my growth. I tried everything. Nothing felt precise enough. Until I went deeper into Vedic numerology and discovered that my birth date had been pointing to specific crystals my entire life &mdash; crystals I had never worn.
                </p>
                <p>
                  The shift was undeniable. So I learned to read it for others.
                </p>
                <p>
                  Pancha Healing exists because everyone deserves a prescription &mdash; not a guess.
                </p>
              </div>
              <div className="ph-divider-cream mt-8 w-24" />
              <div className="mt-5 text-xs tracking-[0.3em] uppercase" style={{ color: "#D4A855" }}>
                &mdash; Madhuram, Founder &middot; Pancha Healing &middot; India
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA BANNER */}
      <section className="relative overflow-hidden py-20 md:py-28 hero-mystical" data-testid="final-cta">
        <Particles variant="violet" />
        <div className="relative max-w-3xl mx-auto px-5 md:px-10 text-center">
          <div className="ph-overline" style={{ color: "#D4A855" }}>Your stones are waiting</div>
          <h2 className="font-display text-3xl md:text-5xl mt-4 text-white leading-tight">
            The bracelet that already belongs to you.
          </h2>
          <p className="font-serif italic text-lg md:text-xl mt-5 max-w-xl mx-auto" style={{ color: "#f6efe0" }}>
            Your stones already exist. They&rsquo;re just not on your wrist yet.
          </p>
          <div className="mt-9">
            <Link to="/prescription" className="ph-cta ph-cta-lg" data-testid="final-cta-btn">
              Begin Your Healing Journey
              <span className="arrow" aria-hidden>&rarr;</span>
            </Link>
            <p className="mt-4 text-[11px] tracking-[0.3em] uppercase" style={{ color: "rgba(246,239,224,0.6)" }}>
              One price &middot; Rs {PRODUCT.price} &middot; Everything included
            </p>
          </div>
          <div className="mt-10 flex items-center justify-center" aria-hidden>
            <img src="/pancha-logo.png" alt="Pancha Healing" className="w-32 md:w-40 rounded-xl shadow-lg" style={{ boxShadow: "0 0 30px rgba(212,168,85,0.35)" }} />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[rgba(106,13,173,0.08)] py-10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex flex-wrap items-center justify-between gap-4">
          <div className="text-[#6B5E7E] text-sm">&copy; {new Date().getFullYear()} Pancha Healing. India.</div>
          <div className="flex items-center gap-5">
            <a href={`https://wa.me/${BRAND.whatsapp}`} target="_blank" rel="noreferrer" className="text-sm text-[#6A0DAD] hover:underline" data-testid="footer-whatsapp">
              WhatsApp: {BRAND.whatsappDisplay}
            </a>
            <a href={`mailto:${BRAND.email}`} className="text-sm text-[#6A0DAD] hover:underline" data-testid="footer-email">
              {BRAND.email}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
